import { useEffect, useMemo, useRef, useState } from "react";
import { embedDashboard } from "@superset-ui/embedded-sdk";
import { FilterBar, type FilterDef } from "../../../components/AthenaPricingEngine/filter-bar";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import {
  fetchSupersetGuestToken,
  useSupersetFilterOptions,
  useSupersetSheets,
  type SupersetFilters,
  type SupersetSheetKey,
} from "../lib/api/superset";

/** Height of the embedded dashboard panel. Fixed rather than viewport-relative
 * since this page has no other content competing for space, and the sheet is
 * tall: office x borrower industry x rating is a lot of rows. */
const EMBED_HEIGHT = 900;

/**
 * Width of Superset's collapsed native filter bar — the narrow strip down the left
 * of the dashboard carrying an expand arrow and a funnel icon.
 *
 * The iframe is shifted left by this much and clipped, because there is no
 * supported way to turn that strip off in Superset 4.1.1: the SDK's
 * `filters.visible: false` becomes `?show_filters=false`, which is declared in
 * Superset's URL_PARAMS and read nowhere. Only `expanded: false` does anything,
 * and that just leaves it collapsed. The frame is cross-origin so its DOM cannot
 * be styled from here — geometry is the only lever.
 *
 * Nothing interactive is hidden: these dashboards have no native filters at all,
 * because the page owns them and applies them as guest-token RLS. Same constant,
 * same reasoning as PRC's SupersetEmbed.
 */
const SUPERSET_FILTER_RAIL_PX = 32;

const ALL_INDUSTRIES = "All Industries";
const ALL_OFFICES = "All Offices";

export default function Snapshot() {
  const sheetsQuery = useSupersetSheets();
  const optionsQuery = useSupersetFilterOptions();
  const { supersetDomain, sheets, defaultSheet } = sheetsQuery.data ?? {};
  const options = optionsQuery.data;

  const [sheetKey, setSheetKey] = useState<SupersetSheetKey | null>(null);
  // Settle on the backend's default once the registry arrives, but never clobber
  // a choice the user has already made.
  const active = sheetKey ?? defaultSheet ?? null;
  const sheet = useMemo(() => sheets?.find((s) => s.key === active), [sheets, active]);
  const uuid = sheet?.uuid;

  // Each filter holds null until the user picks, and falls back to its default
  // below. Storing the default instead would mean re-deriving it — and remounting
  // the iframe — every time the option lists refetched.
  const [asOf, setAsOf] = useState<string | null>(null);
  const [industry, setIndustry] = useState<string>(ALL_INDUSTRIES);
  const [officeLabel, setOfficeLabel] = useState<string>(ALL_OFFICES);

  // Newest snapshot by default: `dates` comes back newest-first.
  const dateValue = asOf ?? options?.dates?.[0] ?? "";
  const officeNumber = options?.offices.find((o) => o.officeLabel === officeLabel)?.officeNumber;

  const filters: SupersetFilters = {
    asOf: dateValue || undefined,
    borrowerIndustry: industry === ALL_INDUSTRIES ? undefined : industry,
    office: officeNumber ?? "",
  };

  const filterDefs: FilterDef[] = [
    {
      id: "date",
      label: "Date",
      value: dateValue,
      options: options?.dates ?? [],
      onChange: setAsOf,
    },
    {
      id: "industry",
      label: "Borrower Industry",
      value: industry,
      options: [ALL_INDUSTRIES, ...(options?.industries ?? [])],
      onChange: setIndustry,
    },
    {
      id: "office",
      label: "Office",
      value: officeLabel,
      options: [ALL_OFFICES, ...(options?.offices ?? []).map((o) => o.officeLabel)],
      onChange: setOfficeLabel,
    },
  ];

  const mount = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // The effect remounts the dashboard whenever the filters change, so it needs a
  // value that is stable across renders when the CONTENT has not changed — the
  // object literal above is a new reference every render and would remount the
  // iframe on each one.
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    // Waits for the option lists too, not just the embed details: mounting first
    // and applying the default date once they arrived would tear the iframe down
    // and rebuild it a second time on every page load.
    if (!uuid || !supersetDomain || !active || !dateValue || !mount.current) return;

    let cancelled = false;
    let unmount: (() => void) | undefined;
    setError(null);
    setReady(false);

    embedDashboard({
      id: uuid,
      supersetDomain,
      mountPoint: mount.current,
      // Reads filtersKey, not `filters`, so the token the SDK re-mints on expiry
      // carries the same RLS as the mount it belongs to.
      fetchGuestToken: () => fetchSupersetGuestToken(active, JSON.parse(filtersKey)),
      iframeTitle: sheet?.label,
      dashboardUiConfig: {
        hideTitle: true,
        hideTab: true,
        // MUST stay false: this is what keeps the per-chart "..." menu, and so the
        // Download > Export to .CSV / Export to Excel items, reachable in-frame.
        hideChartControls: false,
        // No filter bar inside the frame — the page owns the filters and applies
        // them as RLS; a second set of controls could only contradict them.
        // `visible: false` is a dead param in 4.1.1, hence the rail clipping below.
        filters: { expanded: false, visible: false },
      },
    })
      .then((embedded) => {
        if (cancelled) {
          embedded.unmount();
          return;
        }
        unmount = embedded.unmount;
        // The SDK appends a bare iframe with no dimensions of its own; without
        // this the frame collapses to a few pixels whatever the container's height.
        const frame = mount.current?.querySelector("iframe");
        if (frame) {
          // Overwide and pulled left so Superset's collapsed filter strip lands
          // outside the clipping wrapper, rather than trading it for 32px of
          // lost sheet.
          frame.style.width = `calc(100% + ${SUPERSET_FILTER_RAIL_PX}px)`;
          frame.style.marginLeft = `-${SUPERSET_FILTER_RAIL_PX}px`;
          frame.style.height = "100%";
          frame.style.border = "0";
        }
        setReady(true);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });

    return () => {
      cancelled = true;
      try {
        unmount?.();
      } catch {
        /* the iframe may already be gone with the unmounted node */
      }
    };
    // Re-runs on every toggle AND every filter change: the two sheets are separate
    // dashboards, and the filters live in the guest token's RLS — the SDK has no
    // API to swap a token or push a filter into a mounted dashboard.
  }, [uuid, supersetDomain, active, dateValue, filtersKey, sheet?.label]);

  const pending = sheetsQuery.isPending || optionsQuery.isPending;
  const failed = sheetsQuery.isError || optionsQuery.isError;
  const message = pending
    ? "Loading sheets…"
    : failed
      ? "Couldn't load the sheets. Try again."
      : error
        ? error
        : !uuid
          ? `The ${sheet?.label ?? "selected"} sheet is not registered for embedding in Superset.`
          : !ready
            ? "Connecting to Superset…"
            : null;

  return (
    <div>
      <PageHeader
        title="Snapshot"
        subtitle="Published pricing by office, borrower industry, rating and term"
        right={
          sheets && sheets.length > 1 && active ? (
            <ToggleGroup
              type="single"
              value={active}
              onValueChange={(value) => value && setSheetKey(value as SupersetSheetKey)}
              className="w-fit border border-border bg-secondary"
            >
              {sheets.map((s) => (
                <ToggleGroupItem key={s.key} value={s.key} className="h-9 px-4 text-sm">
                  {s.label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          ) : null
        }
      />

      <FilterBar filters={filterDefs} className="mt-6" />

      <div
        className="relative mt-6 overflow-hidden rounded-lg border border-border bg-card"
        style={{ height: EMBED_HEIGHT }}
      >
        <div ref={mount} className="h-full w-full" />
        {message && (
          <div className="absolute inset-0 flex items-center justify-center bg-card text-sm text-muted-foreground">
            {message}
          </div>
        )}
      </div>
    </div>
  );
}
